import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spinner } from '../components/widgets';
import { PageWrapper } from '../components/partials';
import IntegrationHomepage from './containers/IntegrationHomepage';
import IntegrationRequestForm from './containers/IntegrationRequestForm';
import { HIDDEN_STATUSES, REQUEST_TYPES } from './consts';
import { getStatusForType, isRequestDisabled, isFormType } from './utils';
import { getAdminModeEnabled, getIsUserAdmin } from '../selectors/user';

const BASE_PATH = '/react/partners-integration';

const PartnerIntegration = ({ requests, loading, loaded, error = null }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const adminMode = useSelector(getAdminModeEnabled);
    const isStaff = useSelector(getIsUserAdmin);
    const canViewHiddenStatus = adminMode || isStaff;

    const params = new URLSearchParams(location.search);
    const requestedType = (params.get('type') || '').trim().toLowerCase();
    const rawRequestId = params.get('requestId');
    const requestId = rawRequestId ? Number(rawRequestId) : null;

    const isRouteTypeValid = Boolean(
        requestedType && Object.values(REQUEST_TYPES).includes(requestedType) && isFormType(requestedType)
    );
    const { status } = getStatusForType(requests, requestedType);
    const isHiddenStatus = HIDDEN_STATUSES.includes(status);

    const hasValidationData = loaded && !loading && !error;
    const isRequestedTypeDisabled = isRequestDisabled({
        requests,
        type: requestedType,
        status,
    });
    const isValidRoute =
        isRouteTypeValid &&
        (!!requestId || !hasValidationData || !isRequestedTypeDisabled) &&
        (!isHiddenStatus || canViewHiddenStatus);

    useEffect(() => {
        if (isRouteTypeValid && loaded && !isValidRoute) {
            navigate(BASE_PATH, { replace: true });
        }
    }, [isRouteTypeValid, isValidRoute, loaded, navigate]);

    if (isRouteTypeValid && loading && !loaded) {
        return (
            <PageWrapper>
                <Spinner expanded show />
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            {isValidRoute ? (
                <IntegrationRequestForm type={requestedType} requestType={requestedType} requestId={requestId} />
            ) : (
                <IntegrationHomepage />
            )}
        </PageWrapper>
    );
};

PartnerIntegration.propTypes = {
    requests: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            request_type: PropTypes.string,
            status: PropTypes.string,
            updated_at: PropTypes.string,
        })
    ).isRequired,
    loading: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    error: PropTypes.string,
};

export default PartnerIntegration;
